import { useState, useEffect, useRef } from 'react';
import { Save, Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';
import { useSettings } from '../../context/SettingsContext';

export default function AdminSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { refreshSettings } = useSettings();
  const logoInputRef = useRef(null);
  
  const [settings, setSettings] = useState({
    logo: '',
    favicon: '',
    company_info: {
      name: 'NYRAV TOURS AND TRAVELS',
      address: '',
      phone: '',
      email: '',
      tagline: ''
    },
    social_media: {
      facebook: '',
      instagram: '',
      twitter: '',
      youtube: ''
    },
    whatsapp_number: ''
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await adminApi.getSettings();
      if (response.data) {
        setSettings(prev => ({
          ...prev,
          ...response.data,
          company_info: {
            ...prev.company_info,
            ...response.data.company_info
          },
          social_media: {
            ...prev.social_media,
            ...response.data.social_media
          }
        }));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateSettings(settings);
      toast.success('Settings saved successfully');
      refreshSettings();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size should be less than 2MB');
      return;
    }

    // Convert to base64 for preview and storage
    const reader = new FileReader();
    reader.onload = (event) => {
      setSettings(prev => ({
        ...prev,
        logo: event.target?.result
      }));
      toast.success('Logo uploaded. Click Save to apply changes.');
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setSettings(prev => ({
      ...prev,
      logo: ''
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-settings">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Settings</h1>
          <p className="text-[#4A4A4A]">Manage your site settings</p>
        </div>
        <Button 
          onClick={handleSave} 
          className="bg-[#FF6B35] hover:bg-[#E85A2D]" 
          disabled={saving}
          data-testid="save-settings-btn"
        >
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid gap-6">
        {/* Logo & Branding */}
        <Card>
          <CardHeader>
            <CardTitle>Logo & Branding</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-6">
              {/* Logo Preview */}
              <div className="flex-shrink-0">
                <Label className="text-sm text-[#4A4A4A] mb-2 block">Company Logo</Label>
                <div className="relative">
                  {settings.logo ? (
                    <div className="relative w-48 h-24 bg-gray-100 rounded-xl overflow-hidden border-2 border-dashed border-gray-200">
                      <img 
                        src={settings.logo} 
                        alt="Company Logo" 
                        className="w-full h-full object-contain p-2"
                      />
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div 
                      onClick={() => logoInputRef.current?.click()}
                      className="w-48 h-24 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-[#FF6B35] hover:bg-[#FF6B35]/5 transition-colors"
                    >
                      <ImageIcon className="w-8 h-8 text-gray-400 mb-1" />
                      <span className="text-sm text-gray-500">Click to upload</span>
                    </div>
                  )}
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-2">Recommended: 200x60px, PNG or SVG</p>
                
                {/* Or enter URL */}
                <div className="mt-3">
                  <Label className="text-xs text-[#4A4A4A] mb-1 block">Or enter logo URL</Label>
                  <Input
                    value={settings.logo || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, logo: e.target.value }))}
                    placeholder="https://example.com/logo.png"
                    className="text-sm"
                    data-testid="logo-url-input"
                  />
                </div>
              </div>

              {/* Preview */}
              <div className="flex-1">
                <Label className="text-sm text-[#4A4A4A] mb-2 block">Preview</Label>
                <div className="bg-[#004E64] rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    {settings.logo ? (
                      <img 
                        src={settings.logo} 
                        alt="Logo Preview" 
                        className="h-10 w-auto object-contain"
                      />
                    ) : (
                      <div className="w-11 h-11 bg-gradient-to-br from-[#FF6B35] to-[#E85A2D] rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-xl">
                          {(settings.company_info?.name || 'N')[0]}
                        </span>
                      </div>
                    )}
                    <div>
                      <span className="text-white font-bold text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {settings.company_info?.name?.split(' ')[0] || 'NYRAV'}
                      </span>
                      <span className="text-white/60 text-xs block -mt-1 tracking-widest">
                        {settings.company_info?.name?.split(' ').slice(1).join(' ') || 'TOURS & TRAVELS'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Company Info */}
        <Card>
          <CardHeader>
            <CardTitle>Company Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-[#4A4A4A] mb-2 block">Company Name</Label>
                <Input 
                  value={settings.company_info?.name || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    company_info: { ...settings.company_info, name: e.target.value }
                  })}
                  placeholder="NYRAV TOURS AND TRAVELS"
                  data-testid="company-name-input"
                />
              </div>
              <div>
                <Label className="text-sm text-[#4A4A4A] mb-2 block">Tagline</Label>
                <Input 
                  value={settings.company_info?.tagline || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    company_info: { ...settings.company_info, tagline: e.target.value }
                  })}
                  placeholder="Your Gateway to Unforgettable Adventures"
                  data-testid="tagline-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm text-[#4A4A4A] mb-2 block">Address</Label>
              <Textarea 
                value={settings.company_info?.address || ''}
                onChange={(e) => setSettings({
                  ...settings,
                  company_info: { ...settings.company_info, address: e.target.value }
                })}
                placeholder="44, PB Road, Suhagpur, Rehabari, Guwahati-08"
                data-testid="address-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-[#4A4A4A] mb-2 block">Phone</Label>
                <Input 
                  value={settings.company_info?.phone || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    company_info: { ...settings.company_info, phone: e.target.value }
                  })}
                  placeholder="+91 9181268900"
                  data-testid="phone-input"
                />
              </div>
              <div>
                <Label className="text-sm text-[#4A4A4A] mb-2 block">Email</Label>
                <Input 
                  type="email"
                  value={settings.company_info?.email || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    company_info: { ...settings.company_info, email: e.target.value }
                  })}
                  placeholder="nyravtravels@gmail.com"
                  data-testid="email-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* WhatsApp */}
        <Card>
          <CardHeader>
            <CardTitle>WhatsApp Contact</CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <Label className="text-sm text-[#4A4A4A] mb-2 block">WhatsApp Number</Label>
              <Input 
                value={settings.whatsapp_number || ''}
                onChange={(e) => setSettings({
                  ...settings,
                  whatsapp_number: e.target.value
                })}
                placeholder="+919181268900"
                data-testid="whatsapp-input"
              />
              <p className="text-xs text-gray-400 mt-1">Include country code (e.g., +91 for India)</p>
            </div>
          </CardContent>
        </Card>

        {/* Social Media */}
        <Card>
          <CardHeader>
            <CardTitle>Social Media Links</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-[#4A4A4A] mb-2 block">Facebook</Label>
                <Input 
                  value={settings.social_media?.facebook || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    social_media: { ...settings.social_media, facebook: e.target.value }
                  })}
                  placeholder="https://facebook.com/yourpage"
                />
              </div>
              <div>
                <Label className="text-sm text-[#4A4A4A] mb-2 block">Instagram</Label>
                <Input 
                  value={settings.social_media?.instagram || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    social_media: { ...settings.social_media, instagram: e.target.value }
                  })}
                  placeholder="https://instagram.com/yourprofile"
                />
              </div>
              <div>
                <Label className="text-sm text-[#4A4A4A] mb-2 block">Twitter / X</Label>
                <Input 
                  value={settings.social_media?.twitter || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    social_media: { ...settings.social_media, twitter: e.target.value }
                  })}
                  placeholder="https://twitter.com/yourhandle"
                />
              </div>
              <div>
                <Label className="text-sm text-[#4A4A4A] mb-2 block">YouTube</Label>
                <Input 
                  value={settings.social_media?.youtube || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    social_media: { ...settings.social_media, youtube: e.target.value }
                  })}
                  placeholder="https://youtube.com/yourchannel"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
